/**
 * Area `blocked-number` of the console gateway — over `apps/api/src/routes/blocked-number/` (design §40.5, §40.19.2,
 * DD29-2). Owned by plan-029 unit **WAPP-PROT**: the workspace's blocked numbers — the page of those that apply to one
 * VoiceAgent, add one by number, remove one, and "Block this number" from a conversation (ADR 0081, proposed; D-6).
 *
 * ```
 *   listBlockedNumbers        `listBlockedNumbers`         GET    /v1/blocked-numbers?voice_agent_id=&cursor=
 *   addBlockedNumber          `addBlockedNumber`           POST   /v1/blocked-numbers
 *   removeBlockedNumber       `removeBlockedNumber`        DELETE /v1/blocked-numbers/{party_block_entry_id}
 *   blockConversationNumber   `blockConversationNumber`    POST   /v1/conversations/{conversation_id}/block-number
 * ```
 *
 * NEVER A NUMBER BACK. An entry is its last three digits and its label (`last_three_digit` is `null` for a caller on the
 * customer's own phone system known only by what the trunk sent). A typed number goes up once, in the add body, and the
 * api keeps only its digest; "Block this number" sends a conversation id, never a number.
 *
 * Refusals raise `PartyRefusedError` (from `./caller-protection.ts`) with the rule a `400 validation_failed` broke.
 * The register is EMPTY: every operation is in the assembled spec. The sandbox half is `../sandbox/blocked-number.ts`.
 */
import { type WeTalkApiClient } from "@wetalk/api-client";

import type { ConsoleGateway } from "../gateway.js";
import { MalformedResponseError, readArray, readBoolean, readRecord, readText } from "../decode.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";
import { partyBodyOf } from "./caller-protection.js";

export const BLOCKED_NUMBER_SCOPE = ["every_voice_agent", "one_voice_agent"] as const;
export type BlockedNumberScope = (typeof BLOCKED_NUMBER_SCOPE)[number];

/** A day, a week, 30 days — or `null`, until removed (design §40.19.2). */
export const BLOCKED_NUMBER_EXPIRY_HOUR = [24, 168, 720] as const;
export type BlockedNumberExpiryHour = (typeof BLOCKED_NUMBER_EXPIRY_HOUR)[number];

/** At most this many active blocked numbers in a workspace (`422 blocked_number_limit_reached`). */
export const BLOCKED_NUMBER_MOST = 5_000;

/** One blocked number (`PartyBlockEntry`) — never a number, never a digest. */
export interface BlockedNumberView {
  readonly partyBlockEntryId: string;
  /** The number's last three digits; `null` for a caller on the customer's own phone system known by its trunk. */
  readonly lastThreeDigit: string | null;
  readonly label: string | null;
  readonly scope: BlockedNumberScope;
  /** The one VoiceAgent that refuses it, with `scope` `one_voice_agent`. */
  readonly voiceAgentId: string | null;
  readonly digestKind: "e164" | "sip_trunk_wire";
  readonly sourceConversationId: string | null;
  readonly addedByPersonId: string;
  readonly addedAt: string;
  /** When the block lapses; `null` until it is removed. */
  readonly expiresAt: string | null;
}

/** One page of blocked numbers, newest first. */
export interface BlockedNumberPageView {
  readonly entry: readonly BlockedNumberView[];
  readonly nextCursor: string | null;
  /** `false` while blocking numbers is not available yet in this deploy. */
  readonly available: boolean;
}

/** A typed number to block (`BlockedNumberAddition`). */
export interface BlockedNumberAddition {
  /** In international form, or national with `region`. Sent once; never kept by the console. */
  readonly number: string;
  readonly region: string | null;
  readonly label: string | null;
  readonly scope: BlockedNumberScope;
  /** Required with `one_voice_agent`; `null` with `every_voice_agent`. */
  readonly voiceAgentId: string | null;
  readonly expiryHour: BlockedNumberExpiryHour | null;
}

/** "Block this number" from a conversation (`ConversationNumberBlock`). */
export interface ConversationNumberBlock {
  readonly label: string | null;
  readonly scope: BlockedNumberScope;
  readonly voiceAgentId: string | null;
  readonly expiryHour: BlockedNumberExpiryHour | null;
  /** Also put the caller's number on the do-not-call list — an explicit second act, never automatic. */
  readonly alsoDoNotCall: boolean;
}

/** What an add answers: the entry, and `created: false` when the number was already blocked in that scope. */
export interface BlockedNumberAddedView {
  readonly entry: BlockedNumberView;
  readonly created: boolean;
}

declare module "../gateway.js" {
  interface ConsoleGateway {
    /**
     * plan-029 API-PROT: one page of the workspace's active blocked numbers, newest first — with `voiceAgentId`, those
     * that VoiceAgent refuses (its own and those for every VoiceAgent).
     */
    listBlockedNumbers(voiceAgentId: string | null, cursor: string | null): Promise<BlockedNumberPageView>;
    /** plan-029 API-PROT: block a typed number. Raises `PartyRefusedError` (`number_invalid`, limits, unavailable). */
    addBlockedNumber(addition: BlockedNumberAddition): Promise<BlockedNumberAddedView>;
    /** plan-029 API-PROT: remove a blocked number. */
    removeBlockedNumber(partyBlockEntryId: string): Promise<void>;
    /**
     * plan-029 API-PROT: "Block this number" — the conversation's caller, by its id; the number never crosses. Raises
     * `PartyRefusedError` `block_unavailable` when the caller has no number to block (withheld, web).
     */
    blockConversationNumber(conversationId: string, block: ConversationNumberBlock): Promise<BlockedNumberAddedView>;
  }
}

/** The methods this slice puts on `ConsoleGateway`. */
export type BlockedNumberGatewaySlice = Pick<
  ConsoleGateway,
  "listBlockedNumbers" | "addBlockedNumber" | "removeBlockedNumber" | "blockConversationNumber"
>;

/** This area's rows of `GATEWAY_GAP`: none — every operation is served. */
export const blockedNumberGap: readonly GatewayGap[] = [];

// ── the wire ─────────────────────────────────────────────────────────────────────────────────────────────

function oneOf<T extends string>(allowed: readonly T[], value: unknown, path: string): T {
  const raw = readText(value, path);
  const found = allowed.find((one) => one === raw);
  if (found === undefined) throw new MalformedResponseError(path, allowed.join(" | "), raw);
  return found;
}

function nullableText(value: unknown, path: string): string | null {
  return value === null ? null : readText(value, path);
}

/** One `PartyBlockEntry`. */
export function blockedNumberOf(wire: unknown, path: string): BlockedNumberView {
  const row = readRecord(wire, path);
  return {
    partyBlockEntryId: readText(row["party_block_entry_id"], `${path}.party_block_entry_id`),
    lastThreeDigit: nullableText(row["last_three_digit"], `${path}.last_three_digit`),
    label: nullableText(row["label"], `${path}.label`),
    scope: oneOf(BLOCKED_NUMBER_SCOPE, row["scope"], `${path}.scope`),
    voiceAgentId: nullableText(row["voice_agent_id"], `${path}.voice_agent_id`),
    digestKind: oneOf(["e164", "sip_trunk_wire"] as const, row["digest_kind"], `${path}.digest_kind`),
    sourceConversationId: nullableText(row["source_conversation_id"], `${path}.source_conversation_id`),
    addedByPersonId: readText(row["added_by_person_id"], `${path}.added_by_person_id`),
    addedAt: readText(row["added_at"], `${path}.added_at`),
    expiresAt: nullableText(row["expires_at"], `${path}.expires_at`),
  };
}

function addedOf(body: unknown, operationId: string): BlockedNumberAddedView {
  const data = readRecord(readRecord(body, operationId)["data"], `${operationId}.data`);
  return { entry: blockedNumberOf(data, `${operationId}.data`), created: readBoolean(data["created"], `${operationId}.data.created`) };
}

/** The page size asked for: the api's maximum. */
const PAGE_LIMIT = 100;

export function blockedNumberApiSlice(client: WeTalkApiClient, _context: SliceContext): BlockedNumberGatewaySlice {
  return {
    async listBlockedNumbers(voiceAgentId, cursor) {
      const query: { limit: number; voice_agent_id?: string; cursor?: string } = { limit: PAGE_LIMIT };
      if (voiceAgentId !== null) query.voice_agent_id = voiceAgentId;
      if (cursor !== null) query.cursor = cursor;
      const body = readRecord(partyBodyOf(await client.listBlockedNumbers({ query })), "listBlockedNumbers");
      return {
        entry: readArray(body["data"], "listBlockedNumbers.data").map((one, index) =>
          blockedNumberOf(one, `listBlockedNumbers.data[${String(index)}]`),
        ),
        nextCursor: nullableText(body["next_cursor"], "listBlockedNumbers.next_cursor"),
        available: readBoolean(body["available"], "listBlockedNumbers.available"),
      };
    },
    async addBlockedNumber(addition) {
      const answer = await client.addBlockedNumber({
        body: {
          number: addition.number,
          region: addition.region,
          label: addition.label,
          scope: addition.scope,
          voice_agent_id: addition.voiceAgentId,
          expiry_hour: addition.expiryHour,
        },
      });
      return addedOf(partyBodyOf(answer), "addBlockedNumber");
    },
    async removeBlockedNumber(partyBlockEntryId) {
      partyBodyOf(await client.removeBlockedNumber({ path: { party_block_entry_id: partyBlockEntryId } }));
    },
    async blockConversationNumber(conversationId, block) {
      const answer = await client.blockConversationNumber({
        path: { conversation_id: conversationId },
        body: {
          label: block.label,
          scope: block.scope,
          voice_agent_id: block.voiceAgentId,
          expiry_hour: block.expiryHour,
          also_do_not_call: block.alsoDoNotCall,
        },
      });
      return addedOf(partyBodyOf(answer), "blockConversationNumber");
    },
  };
}

/** Query keys of this area: the pages per VoiceAgent (or the whole workspace, `null`). */
export const BLOCKED_NUMBER_QUERY_KEY = {
  every: ["blocked-number"] as const,
  forVoiceAgent: (voiceAgentId: string | null) => ["blocked-number", voiceAgentId ?? "workspace"] as const,
};
